import {
  collection,
  getDocs,
  query,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { EquipmentType } from '../types/firestore';
import {
  buildEquipmentTypeCatalogIndex,
  type EquipmentTypeCatalogEntry,
  type EquipmentTypeCatalogIndex,
} from '../utils/equipmentTypeCatalog';

interface EquipmentTypeCatalogResult {
  entries: EquipmentTypeCatalogEntry[];
  index: EquipmentTypeCatalogIndex;
}

const EQUIPMENT_TYPES_COLLECTION = 'equipmentTypes';
const EQUIPMENT_TYPE_CACHE_TTL_MS = 5 * 60 * 1000;

let activeEquipmentTypeCatalogCache: {
  expiresAt: number;
  value: EquipmentTypeCatalogResult;
} | null = null;

const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';

const asStatus = (value: unknown): 'active' | 'inactive' | undefined => {
  const normalized = asTrimmedString(value).toLowerCase();
  if (normalized === 'active' || normalized === 'inactive') {
    return normalized;
  }
  return undefined;
};

const mapEquipmentTypeDoc = (
  docSnap: QueryDocumentSnapshot<DocumentData>
): EquipmentTypeCatalogEntry | null => {
  const data = docSnap.data() as Record<string, unknown>;
  const name = asTrimmedString(data.name);
  const shortCode = asTrimmedString(data.shortCode);
  if (!name || !shortCode) return null;

  return {
    id: docSnap.id,
    equipmentTypeId: asTrimmedString(data.equipmentTypeId) || docSnap.id,
    name,
    nameKey: asTrimmedString(data.nameKey),
    shortCode,
    shortCodeKey: asTrimmedString(data.shortCodeKey),
    status: asStatus(data.status),
    source: data.source === 'suggestion' ? 'suggestion' : 'admin',
    sourceSuggestionId: asTrimmedString(data.sourceSuggestionId) || null,
  };
};

const mapEquipmentTypeDocToDomain = (
  docSnap: QueryDocumentSnapshot<DocumentData>
): EquipmentType | null => {
  const entry = mapEquipmentTypeDoc(docSnap);
  if (!entry) return null;

  const data = docSnap.data() as Record<string, unknown>;
  return {
    uid: docSnap.id,
    id: docSnap.id,
    equipmentTypeId: entry.equipmentTypeId || docSnap.id,
    name: entry.name,
    nameKey: entry.nameKey,
    shortCode: entry.shortCode,
    shortCodeKey: entry.shortCodeKey,
    status: entry.status || 'active',
    source: entry.source || 'admin',
    sourceSuggestionId: entry.sourceSuggestionId,
    createdBy: asTrimmedString(data.createdBy),
    updatedBy: asTrimmedString(data.updatedBy),
    createdAt: data.createdAt as EquipmentType['createdAt'],
    updatedAt: data.updatedAt as EquipmentType['updatedAt'],
    activatedAt: data.activatedAt as EquipmentType['activatedAt'],
    deactivatedAt: data.deactivatedAt as EquipmentType['deactivatedAt'],
  };
};

const buildCatalogResult = (entries: EquipmentTypeCatalogEntry[]): EquipmentTypeCatalogResult => {
  const index = buildEquipmentTypeCatalogIndex(entries);
  return {
    entries: index.entries,
    index,
  };
};

export const getActiveEquipmentTypeCatalog = async (options?: {
  forceRefresh?: boolean;
}): Promise<EquipmentTypeCatalogResult> => {
  const shouldUseCache =
    options?.forceRefresh !== true &&
    activeEquipmentTypeCatalogCache &&
    activeEquipmentTypeCatalogCache.expiresAt > Date.now();

  if (shouldUseCache && activeEquipmentTypeCatalogCache) {
    return activeEquipmentTypeCatalogCache.value;
  }

  const snapshot = await getDocs(
    query(collection(firestore, EQUIPMENT_TYPES_COLLECTION), where('status', '==', 'active'))
  );

  const entries = snapshot.docs
    .map((docSnap) => mapEquipmentTypeDoc(docSnap))
    .filter((entry): entry is EquipmentTypeCatalogEntry => Boolean(entry));

  const value = buildCatalogResult(entries);

  activeEquipmentTypeCatalogCache = {
    expiresAt: Date.now() + EQUIPMENT_TYPE_CACHE_TTL_MS,
    value,
  };

  return value;
};

export const getEquipmentTypeCatalogForAdmin = async (): Promise<EquipmentType[]> => {
  const snapshot = await getDocs(collection(firestore, EQUIPMENT_TYPES_COLLECTION));
  return snapshot.docs
    .map((docSnap) => mapEquipmentTypeDocToDomain(docSnap))
    .filter((entry): entry is EquipmentType => Boolean(entry))
    .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
};

export const clearEquipmentTypeCatalogCache = (): void => {
  activeEquipmentTypeCatalogCache = null;
};
